import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../../utils/api';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { CreateBookingModal } from '../../components/CreateBookingModal';
import { BookingDetailsModal } from '../../components/BookingDetailsModal';
import { CalendarIcon, List, ExternalLink, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import './calendar.css';

const locales = {
  de: de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export function BookingsCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [view, setView] = useState('calendar'); // 'calendar' or 'table'
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  // Check for bookingId or orderId in URL params and switch to table view + highlight
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    const orderId = searchParams.get('orderId');

    if ((bookingId || orderId) && bookings.length > 0) {
      // Switch to table view
      setView('table');

      // Find the booking
      let targetBooking = null;
      if (bookingId) {
        targetBooking = bookings.find(b => (b.bookingId || b.booking_id) === bookingId);
      } else if (orderId) {
        // Find booking by order_id (Shopify order ID)
        // Support both camelCase and snake_case field names
        console.log('[BookingsCalendar] Looking for booking with orderId:', orderId);
        console.log('[BookingsCalendar] Available bookings:', bookings.length);

        targetBooking = bookings.find(b => {
          const bookingOrderId = b.orderId || b.order_id;
          console.log('[BookingsCalendar] Checking booking:', b.bookingId || b.booking_id, 'order_id:', bookingOrderId);
          return bookingOrderId === orderId;
        });

        console.log('[BookingsCalendar] Found booking:', targetBooking);
      }

      // Highlight the booking
      if (targetBooking) {
        const targetBookingId = targetBooking.bookingId || targetBooking.booking_id;
        setTimeout(() => {
          const element = document.getElementById(`booking-${targetBookingId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedBookingId(targetBookingId);

            // Remove highlight after 3 seconds
            setTimeout(() => {
              setHighlightedBookingId(null);
              setSearchParams({});
            }, 3000);
          }
        }, 100);
      } else {
        // Booking not found - show toast with debug info
        console.error('[BookingsCalendar] Booking not found for orderId:', orderId);
        toast.error(`Buchung nicht gefunden für Order ID: ${orderId}`);
        setSearchParams({});
      }
    }
  }, [searchParams, bookings, setSearchParams]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/bookings');
      setBookings(response.bookings || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };


  // Transform bookings to calendar events
  const events = useMemo(() => {
    return bookings
      .filter(booking => booking.status === 'confirmed')
      .map(booking => {
        // Parse dates (API returns camelCase)
        const startDate = new Date(booking.startDate || booking.eventDate);
        const endDate = booking.endDate ? new Date(booking.endDate) : startDate;

        // Multi-day events should end at end of last day
        const calendarEnd = new Date(endDate);
        calendarEnd.setHours(23, 59, 59);

        // Determine color based on product title and variant
        const fullTitle = `${booking.productTitle} ${booking.variantTitle}`.toLowerCase();
        let color = '#3b82f6'; // default blue (Basic)
        if (fullTitle.includes('premium')) {
          color = '#10b981'; // green (Premium)
        } else if (fullTitle.includes('luxury')) {
          color = '#f59e0b'; // amber (Luxury)
        }

        // Add indicator for shipped bookings
        const isShipped = ['shipped', 'delivered'].includes(booking.shippingStatus);

        return {
          id: booking.bookingId,
          title: `${isShipped ? '✓ ' : ''}${booking.customerName || 'Unbekannt'} - ${booking.variantTitle || 'Fotobox'}`,
          start: startDate,
          end: calendarEnd,
          allDay: true,
          resource: booking,
          style: {
            backgroundColor: color,
            border: isShipped ? '2px solid #059669' : 'none',
            fontWeight: isShipped ? 'bold' : 'normal',
          },
        };
      });
  }, [bookings]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };


  const eventStyleGetter = (event) => {
    return {
      style: event.style,
    };
  };

  // Helper functions for table view
  const getDaysUntilEvent = (booking) => {
    const eventDate = new Date(booking.startDate || booking.eventDate);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isCritical = (booking) => {
    return booking.status === 'confirmed' &&
           ['not_shipped', 'preparing'].includes(booking.shippingStatus || 'not_shipped') &&
           getDaysUntilEvent(booking) <= 7 &&
           getDaysUntilEvent(booking) >= 0;
  };

  const getShippingStatusBadge = (status) => {
    const variants = {
      not_shipped: 'warning',
      preparing: 'info',
      shipped: 'success',
      delivered: 'success',
      returned: 'secondary',
      overdue: 'error'
    };

    const labels = {
      not_shipped: 'Nicht versendet',
      preparing: 'Vorbereitung',
      shipped: 'Versendet',
      delivered: 'Zugestellt',
      returned: 'Zurück',
      overdue: 'Überfällig'
    };

    return (
      <Badge variant={variants[status] || 'secondary'} size="sm">
        {labels[status] || status}
      </Badge>
    );
  };

  // Sort bookings for table view (earliest first, critical on top)
  const sortedBookings = useMemo(() => {
    return [...bookings]
      .filter(b => b.status === 'confirmed')
      .sort((a, b) => {
        // Critical bookings first
        const aCritical = isCritical(a);
        const bCritical = isCritical(b);
        if (aCritical && !bCritical) return -1;
        if (!aCritical && bCritical) return 1;

        // Then sort by start date
        const aDate = new Date(a.startDate || a.eventDate);
        const bDate = new Date(b.startDate || b.eventDate);
        return aDate - bDate;
      });
  }, [bookings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lade Buchungen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Buchungskalender</h1>
          <p className="mt-1 text-sm text-gray-500">
            Übersicht aller bestätigten Buchungen
          </p>
        </div>
        <div className="flex gap-4 items-center">
          {/* View Toggle */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setView('calendar')}
              className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                view === 'calendar'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Kalender
            </button>
            <button
              onClick={() => setView('table')}
              className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                view === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              Tabelle
            </button>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: '#3b82f6',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            Buchung erstellen
          </button>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3b82f6' }}></div>
            <span className="text-sm text-gray-600">Basic</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
            <span className="text-sm text-gray-600">Premium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
            <span className="text-sm text-gray-600">Luxury</span>
          </div>
        </div>
      </div>

      {/* Table View */}
      {view === 'table' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buchung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event-Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produkt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event-Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Versand
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tracking
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBookings.map((booking) => {
                  const critical = isCritical(booking);
                  const daysUntil = getDaysUntilEvent(booking);

                  const isHighlighted = highlightedBookingId === booking.bookingId;

                  return (
                    <tr
                      key={booking.bookingId}
                      id={`booking-${booking.bookingId}`}
                      className={`${
                        isHighlighted
                          ? 'bg-blue-100 border-l-4 border-blue-500 animate-pulse'
                          : critical
                          ? 'bg-red-50 border-l-4 border-red-500'
                          : 'hover:bg-gray-50'
                      } transition-colors`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {booking.bookingId?.substring(0, 8)}...
                            </div>
                            {critical && (
                              <div className="text-xs text-red-600 font-semibold mt-1">
                                In {daysUntil} {daysUntil === 1 ? 'Tag' : 'Tagen'}!
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-bold text-blue-600">
                          {booking.eventCode || booking.event_code || '-'}
                        </div>
                        {(booking.eventCodeExpiresAt || booking.event_code_expires_at) &&
                         new Date(booking.eventCodeExpiresAt || booking.event_code_expires_at) < new Date() && (
                          <div className="text-xs text-red-600 mt-1">
                            Abgelaufen
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{booking.customerName || 'Unbekannt'}</div>
                        <div className="text-sm text-gray-500">{booking.customerEmail}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{booking.productTitle}</div>
                        <div className="text-sm text-gray-500">{booking.variantTitle}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {format(new Date(booking.startDate || booking.eventDate), 'dd.MM.yyyy', { locale: de })}
                        </div>
                        {booking.totalDays > 1 && (
                          <div className="text-sm text-gray-500">
                            ({booking.totalDays} Tage)
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getShippingStatusBadge(booking.shippingStatus || 'not_shipped')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {booking.trackingNumber ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-gray-700">{booking.trackingNumber}</span>
                            <a
                              href={`https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${booking.trackingNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setSelectedEvent(booking)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sortedBookings.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Keine bestätigten Buchungen</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <Card className="p-6">
          <div style={{ height: '700px' }}>
            <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            culture="de"
            messages={{
              next: 'Weiter',
              previous: 'Zurück',
              today: 'Heute',
              month: 'Monat',
              week: 'Woche',
              day: 'Tag',
              agenda: 'Agenda',
              date: 'Datum',
              time: 'Zeit',
              event: 'Buchung',
              noEventsInRange: 'Keine Buchungen in diesem Zeitraum',
              showMore: (total) => `+ ${total} weitere`,
            }}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
          />
        </div>
        </Card>
      )}

      {/* Create Booking Modal */}
      {showCreateModal && (
        <CreateBookingModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadBookings(); // Refresh calendar
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Booking Details Modal */}
      {selectedEvent && (
        <BookingDetailsModal
          booking={selectedEvent}
          onClose={handleCloseModal}
          onUpdate={loadBookings}
        />
      )}
    </div>
  );
}
